import type { Point, SceneNode } from "../scene/model";

export const shapeTools = ["rectangle", "ellipse", "polygon"] as const;
export type ShapeTool = (typeof shapeTools)[number];
export interface PolygonParameters {
  sides: number;
  star: boolean;
  innerRatio: number;
}
export function isNativeShape(node: SceneNode) {
  return ["rectangle", "ellipse", "polygon"].includes(node.kind);
}
export function polygonParameters(node: SceneNode): PolygonParameters {
  const { sides, star, innerRatio } = node.geometry;
  if (
    !Number.isInteger(sides) ||
    typeof sides !== "number" ||
    sides < 3 ||
    sides > 64 ||
    typeof star !== "boolean" ||
    typeof innerRatio !== "number" ||
    !Number.isFinite(innerRatio) ||
    innerRatio < 0.01 ||
    innerRatio > 0.99
  )
    throw new Error("Число вершин — от 3 до 64, глубина лучей — от 1 до 99%.");
  return { sides, star, innerRatio };
}
/** Pure normalized geometry. Dimensions always mean the actual local extents. */
export function polygonOutline(parameters: PolygonParameters) {
  const { sides, star, innerRatio } = parameters;
  const count = sides * (star ? 2 : 1);
  const raw = Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / count;
    const radius = star && index % 2 ? innerRatio : 1;
    return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
  });
  const xs = raw.map((p) => p.x),
    ys = raw.map((p) => p.y);
  const left = Math.min(...xs),
    top = Math.min(...ys);
  const width = Math.max(...xs) - left,
    height = Math.max(...ys) - top;
  const normalize = (p: Point) => ({
    x: (p.x - left) / width - 0.5,
    y: (p.y - top) / height - 0.5,
  });
  return { points: raw.map(normalize), center: normalize({ x: 0, y: 0 }) };
}
/** Every regular/star polygon is radial about this center; triangles never overlap. */
export function polygonTriangles(parameters: PolygonParameters) {
  const { points, center } = polygonOutline(parameters);
  return new Float32Array(
    points.flatMap((p, index) => {
      const next = points[(index + 1) % points.length]!;
      return [
        center.x + 0.5,
        center.y + 0.5,
        p.x + 0.5,
        p.y + 0.5,
        next.x + 0.5,
        next.y + 0.5,
      ];
    }),
  );
}
export function polygonKey(node: SceneNode) {
  const p = polygonParameters(node);
  return `${p.sides}:${p.star}:${p.innerRatio}`;
}
export function insidePolygon(points: readonly Point[], p: Point) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[j]!,
      b = points[i]!;
    const cross = (p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x);
    if (
      Math.abs(cross) < 1e-10 &&
      p.x >= Math.min(a.x, b.x) - 1e-10 &&
      p.x <= Math.max(a.x, b.x) + 1e-10 &&
      p.y >= Math.min(a.y, b.y) - 1e-10 &&
      p.y <= Math.max(a.y, b.y) + 1e-10
    )
      return true;
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
}
export function changePolygon(
  nodes: readonly SceneNode[],
  property: keyof PolygonParameters,
  value: number | boolean,
) {
  return nodes
    .filter((n) => n.kind === "polygon")
    .map((node) => {
      const changed = {
        ...node,
        geometry: { ...node.geometry, [property]: value },
      };
      polygonParameters(changed);
      return changed;
    });
}
